import { useState } from 'react';
import { useScrollFadeIn } from '../../../hooks/useScrollFadeIn';

type FaqCategory = {
  label: string;
  icon: string;
  items: FaqItem[];
};

type FaqItem = {
  id: number;
  question: string;
  answer: string;
  link?: { url: string; label: string };
  badge?: string;
};

export default function FAQ() {
  const { ref, isVisible } = useScrollFadeIn({ delay: 250 });
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<number>(0);

  const categories: FaqCategory[] = [
    {
      label: 'キャンセル・返品',
      icon: 'ri-arrow-go-back-line',
      items: [
        {
          id: 1,
          question: '本当に理由を問わず返品できますか？',
          answer: 'はい、商品到着後30日以内であれば理由を問わず返品が可能です。座り心地が合わない・サイズが合わないなどの理由でも問題ありません。\n\n※商品状態に応じて返金額が異なります：\n・未開封：100%\n・開封済み・未組立：75%\n・開封済み・組立済み：50%',
          badge: '30日保証',
        },
        {
          id: 2,
          question: '予約後のキャンセルは可能ですか？',
          answer: 'キャンセル期限：2026年3月31日 23:59まで\n\n期限内であれば、カスタマーサポートへご連絡いただくことでキャンセル手続きができます。期限を過ぎた場合は、30日間返品保証の対象となりますので、商品到着後に返品手続きをお願いいたします。',
          badge: '無料キャンセル',
        },
        {
          id: 3,
          question: 'キャンセル料・手数料はかかりますか？',
          answer: 'キャンセル期限（2026年3月31日）以内のキャンセルは、手数料・キャンセル料は一切かかりません。全額返金いたします。\n\n商品到着後の返品の場合は、返送料のみご負担いただきます（未開封4,400円、開封済み7,700円）。',
          badge: '手数料無料',
        },
        {
          id: 4,
          question: '返品にかかる費用はどうなりますか？',
          answer: '返品時の返送料は以下の通りです：\n\n・未開封：一律 4,400円（返送料）\n・開封済み・組立済み：一律 7,700円（返送料）\n\n※返金手数料は無料です。\n※初期不良の場合は返送料も当社負担です。',
        },
        {
          id: 5,
          question: '開封したらどのくらい返金されますか？',
          answer: '商品状態に応じて以下のように返金されます：\n\n【未開封】100%返金\n【開封済み・未組立】75%返金\n【開封済み・組立済み】50%返金\n\n※返送料は別途お客様負担となります（未開封4,400円、開封済み7,700円）。',
        },
        {
          id: 6,
          question: 'Amazonギフト券などの特典を受け取っている場合は？',
          answer: '以下の特典を受け取っている場合、実費として返金額から差し引かれます：\n\n・Amazonギフト券\n・無料の組立代行サービス（6,600円相当）\n\n例：開封済み・組立済みで返品する場合、商品代金の50%から特典分を差し引いた金額が返金されます。',
        },
        {
          id: 7,
          question: '外箱を捨ててしまったのですが返品できますか？',
          answer: 'はい、返品可能です。当社手配の配送業者様で対応いたします。\n\nお客様で行っていただくことは、商品を玄関までお持ちいただくことのみです。梱包材がなくても問題ありません。',
        },
        {
          id: 8,
          question: '返品できないケースは？',
          answer: '以下の場合は返品不可となります：\n\n・到着から30日を過ぎている\n・著しい汚損・破損がある\n・ペット・煙草の臭いが付着している\n・自己改造を行っている\n・パーツ欠品が大きい場合\n\n通常使用の範囲内であれば問題ありません。',
        },
        {
          id: 9,
          question: '返品申請はどこからできますか？',
          answer: '公式サイトの「返品申請フォーム」またはサポートメールよりお申し込みいただけます。\n\n【サポートメール】support@mever.design\n【受付時間】平日 10:00-18:00\n\n返品申請後、担当者より詳細な手順をご案内いたします。',
          link: { url: 'https://mever.design/pages/contact', label: '返品申請フォームはこちら' },
        },
        {
          id: 10,
          question: '返金はいつ行われますか？',
          answer: '倉庫での検品完了後、7営業日以内に返金処理を行います。\n\n返金方法は銀行振込となります。\n※カード決済はAmazonでの購入のみです（Amazon側で返金処理対応）。',
        },
        {
          id: 11,
          question: '返金対象外のものはありますか？',
          answer: '以下は返金対象外となります：\n\n・配送料\n・手数料\n・ギフトラッピング\n・組立代行サービス（無料特典の場合は差額精算）\n\n商品本体の代金のみが返金対象となります。',
        },
      ],
    },
    {
      label: '保証・初期不良',
      icon: 'ri-shield-check-line',
      items: [
        {
          id: 101,
          question: '5年保証の内容について教えてください。',
          answer: 'MeVER Chair Pro v2には、購入日から5年間の製品保証が付帯しています。通常使用における故障や不具合が発生した場合、無償で修理または交換いたします。\n\n【保証対象の主要部品】\n・フレーム\n・ガスシリンダー\n・リクライニング機構\n・アームレスト\n・ヘッドレスト\n\n消耗品（キャスター・張地の摩耗等）や誤った使用方法による破損は対象外となります。',
          badge: '5年保証',
        },
        {
          id: 102,
          question: '保証期間中に故障した場合、費用はかかりますか？',
          answer: '保証期間内（購入日から5年間）の通常使用による故障・不具合は、修理・交換ともに無償で対応いたします。\n\n送料についても当社が負担いたします。お客様に費用が発生することはありません。',
          badge: '修理・交換無償',
        },
        {
          id: 103,
          question: '初期不良の場合はどうなりますか？',
          answer: '初期不良の可能性がある場合は、以下をお送りください：\n\n・写真\n・動画\n・該当箇所の状況説明\n\n確認後、以下のいずれかを選択できます：\n① 代替品の発送\n② 全額返金（送料含む）\n\n初期不良の場合、返送料は当社が負担いたします。',
        },
        {
          id: 104,
          question: '保証期間が過ぎた後の修理は対応していますか？',
          answer: '保証期間（5年）終了後も、有償にて修理・部品交換に対応しております。\n\n長くご愛用いただけるよう、交換パーツの販売も予定しております。詳細はサポートまでお問い合わせください。\n\n【サポートメール】support@mever.design',
        },
        {
          id: 105,
          question: '保証書は必要ですか？購入証明はどうすれば良いですか？',
          answer: '購入時のご注文確認メールが保証書の代わりとなります。紙の保証書は同梱されませんが、ご注文番号と購入日が確認できるメールを保管しておいてください。\n\n万が一メールを紛失された場合も、ご購入情報をもとに対応いたします。',
        },
      ],
    },
    {
      label: '配送・支払い',
      icon: 'ri-truck-line',
      items: [
        {
          id: 201,
          question: '配送時期と送料について',
          answer: '予約販売商品は2025年4月下旬より順次発送予定です。ご予約順に発送いたしますので、お早めのご予約をおすすめします。\n\n送料は全国一律無料です。配送業者はヤマト運輸を使用し、お届け日時の指定も可能です。発送時にはメールで追跡番号をお知らせいたします。',
        },
        {
          id: 202,
          question: '支払い方法は何がありますか？',
          answer: 'クレジットカード（VISA/MasterCard/JCB/AMEX）、銀行振込、コンビニ決済、Amazon Pay、そしてPaidy（ペイディ）の後払い分割払いに対応しています。\n\nPaidyをご利用いただくと、3回・6回・12回の分割払いが可能で、月々約7,500円〜のお支払いで購入いただけます（12回分割の場合）。分割手数料はお客様負担となります。',
        },
        {
          id: 203,
          question: 'どの配送業者を使えば良いですか？（返品時）',
          answer: '当社指定の配送業者をこちらで手配いたします。お客様は事前の準備だけでOKです。\n\n返品申請後、配送業者の手配と集荷日時の調整についてご連絡いたします。',
        },
        {
          id: 204,
          question: '海外への配送は対応していますか？',
          answer: '現在、国内（日本国内）への配送のみ対応しております。海外への配送は承っておりません。\n\n国内配送は全国一律送料無料です。離島・一部地域については別途ご相談ください。',
        },
      ],
    },
    {
      label: '製品・仕様',
      icon: 'ri-armchair-line',
      items: [
        {
          id: 301,
          question: '椅子の耐荷重・対応身長を教えてください。',
          answer: 'MeVER Chair Pro v2のスペックは以下の通りです：\n\n・耐荷重：150kg\n・対応身長：155cm〜195cm\n・座面高さ調整範囲：42cm〜52cm\n・リクライニング角度：90°〜135°\n\n幅広い体型・身長に対応できるよう設計されています。',
        },
        {
          id: 302,
          question: 'カラーやサイズのカスタマイズはできますか？',
          answer: '現在ご用意しているカラーバリエーションは以下の通りです：\n・ブラック（50脚限定）\n・グレー（50脚限定）\n\n各色50脚の限定販売となります。ご希望のカラーはお早めにご予約ください。',
        },
        {
          id: 303,
          question: '組立代行サービスとは何ですか？',
          answer: '組立代行サービスとは、商品到着後にスタッフがお客様のご自宅へ伺い、椅子の組み立てを行うサービスです。\n\n6,600円（税込）のオプションサービスです。\n※日程はご予約後に別途調整いたします。',
        },
        {
          id: 304,
          question: '法人・まとめ買いの割引はありますか？',
          answer: '法人のお客様や複数台のご購入をご検討の方には、別途お見積もりをご提案しております。\n\n5台以上のご注文で特別価格のご相談が可能です。\n\n【法人・まとめ買いのお問い合わせ】\nsupport@mever.design\n\n件名に「法人購入について」とご記載の上、ご希望台数・ご利用用途をお知らせください。',
          link: { url: 'https://b2b.mever.design/', label: '法人向けページはこちら' },
        },
      ],
    },
  ];

  const toggleFAQ = (key: string) => {
    setOpenIndex(openIndex === key ? null : key);
  };

  const currentItems = categories[activeCategory].items;

  return (
    <section
      ref={ref}
      className={`py-16 md:py-32 px-5 md:px-10 bg-white transition-all duration-1000 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      <div className="max-w-5xl mx-auto px-0 md:px-10">

        {/* ヘッダー */}
        <div className="text-center mb-10 md:mb-16">
          <p className="text-[10px] tracking-[0.4em] uppercase text-gray-400 font-medium mb-3 md:mb-4">
            FAQ
          </p>
          <h2 className="text-2xl md:text-5xl font-bold text-gray-900 tracking-tight">
            よくある質問
          </h2>
          <p className="mt-3 md:mt-5 text-sm md:text-base text-gray-500">
            ご購入前の不安を解消します。お気軽にご確認ください。
          </p>
        </div>

        {/* カテゴリタブ */}
        <div className="flex flex-wrap gap-2 md:gap-3 justify-center mb-8 md:mb-12">
          {categories.map((cat, idx) => (
            <button
              key={idx}
              onClick={() => { setActiveCategory(idx); setOpenIndex(null); }}
              className={`flex items-center gap-1.5 px-4 py-2 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === idx
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${cat.icon} text-sm`}></i>
              </div>
              {cat.label}
            </button>
          ))}
        </div>

        {/* FAQアコーディオン */}
        <div className="space-y-0">
          {currentItems.map((faq, index) => {
            const key = `${activeCategory}-${index}`;
            return (
              <div
                key={faq.id}
                className="border-b border-gray-200 overflow-hidden transition-all duration-300"
              >
                <button
                  onClick={() => toggleFAQ(key)}
                  className="w-full px-0 py-5 md:py-7 flex items-start justify-between gap-3 md:gap-4 text-left cursor-pointer hover:opacity-70 transition-opacity duration-200 active:scale-[0.99] min-h-[56px]"
                >
                  <div className="flex items-start gap-2 flex-1">
                    <h3 className="text-sm md:text-base font-bold text-gray-900 leading-[1.6] md:leading-relaxed flex-1">
                      {faq.question}
                    </h3>
                    {faq.badge && (
                      <span className="flex-shrink-0 mt-0.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200 whitespace-nowrap">
                        {faq.badge}
                      </span>
                    )}
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`ri-add-line text-xl md:text-2xl text-gray-400 transition-transform duration-300 ${
                        openIndex === key ? 'rotate-45' : ''
                      }`}
                    ></i>
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === key ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pb-5 md:pb-8 pr-8 md:pr-10">
                    <p className="text-sm leading-[1.8] whitespace-pre-line" style={{ color: '#6e6e73' }}>
                      {faq.answer}
                    </p>
                    {faq.link && (
                      <a
                        href={faq.link.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 md:px-4 md:py-2 bg-gray-900 hover:bg-gray-700 text-white text-sm md:text-xs font-semibold rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap active:scale-[0.97]"
                      >
                        <i className="ri-external-link-line text-sm"></i>
                        {faq.link.label}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 安心バッジ */}
        <div className="mt-10 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { icon: 'ri-arrow-go-back-line', title: '30日返品保証', sub: '理由不問・安心保証' },
            { icon: 'ri-shield-check-line', title: '5年製品保証', sub: '主要部品すべて対象' },
            { icon: 'ri-close-circle-line', title: '無料キャンセル', sub: '2026年3月末まで' },
            { icon: 'ri-customer-service-2-line', title: '専任サポート', sub: '平日10:00-18:00' },
          ].map((badge, i) => (
            <div key={i} className="flex flex-col items-center gap-2 p-4 md:p-5 bg-gray-50 rounded-2xl border border-gray-100 text-center">
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full border border-gray-200 shadow-sm">
                <i className={`${badge.icon} text-lg text-gray-700`}></i>
              </div>
              <p className="text-xs md:text-sm font-bold text-gray-900 leading-tight">{badge.title}</p>
              <p className="text-[10px] md:text-xs text-gray-500">{badge.sub}</p>
            </div>
          ))}
        </div>

        {/* サポート情報 */}
        <div className="mt-6 md:mt-10 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-3.5 md:px-6 md:py-4 bg-gray-50 rounded-2xl border border-gray-200">
            <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full flex-shrink-0">
              <i className="ri-customer-service-2-line text-lg text-gray-600"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900 mb-0.5">
                その他のご質問はこちら
              </p>
              <p className="text-xs text-gray-500">
                平日 10:00-18:00 | info@mever.design
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
